'use client';

import { Icon } from '@plura/client/components';
import { cn } from '@plura/shared/utils';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import type { TocItem } from './utils';

export interface LatestPost {
  title: string;
  author: string;
  date: string;
  href: string;
}

interface TableOfContentsProps {
  items: TocItem[];
  latestPosts?: LatestPost[];
  shareUrl?: string;
  shareTitle?: string;
}

// Pin the sidebar this far below the viewport top — clears the 80px fixed header.
const STICKY_TOP = 112;

const TableOfContents = ({ items, latestPosts, shareUrl, shareTitle }: TableOfContentsProps) => {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) {
          setActiveId(visible.target.id);
        }
      },
      {
        rootMargin: `-${STICKY_TOP}px 0px -60% 0px`,
        threshold: 0,
      }
    );

    items.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [items]);

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = STICKY_TOP + 20;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const encodedUrl = encodeURIComponent(shareUrl || '');
  const encodedTitle = encodeURIComponent(shareTitle || '');

  const sectionTitleClass =
    'm-0 mb-3 font-body text-[11px] font-bold uppercase tracking-[0.1em] text-[#555555]';

  return (
    <nav
      className="sticky top-[112px] flex max-h-[calc(100vh-132px)] flex-col gap-6 overflow-y-auto pr-1 max-lg:static max-lg:max-h-none max-lg:overflow-visible max-lg:pr-0"
      aria-label="Table of contents"
    >
      <div className="flex flex-col gap-6">
        {items.length > 0 && (
            <div className="flex flex-col">
              <h4 className={sectionTitleClass}>What&apos;s Included</h4>
              <ul className="relative m-0 flex list-none flex-col gap-0 p-0 before:absolute before:bottom-1 before:left-0 before:top-1 before:w-0.5 before:rounded-sm before:bg-[#e5e5e5]">
                {items.map((item) => (
                  <li key={item.id} className="relative">
                    <button
                      type="button"
                      onClick={() => handleClick(item.id)}
                      className={cn(
                        'relative block w-full cursor-pointer border-none bg-transparent py-1.25 pl-3.5 text-left font-body text-[13px] font-normal leading-normal text-[#555555] transition-colors',
                        'before:absolute before:bottom-1 before:left-0 before:top-1 before:w-0.5 before:rounded-sm before:bg-transparent before:transition-colors',
                        'hover:text-dark',
                        activeId === item.id && 'font-semibold text-primary before:bg-primary',
                      )}
                    >
                      {item.text}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {shareUrl && (
            <div>
              <h4 className={sectionTitleClass}>Share</h4>
              <div className="flex gap-2">
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex size-9 items-center justify-center rounded-lg bg-primary text-white no-underline transition-opacity duration-150 hover:opacity-90"
                  aria-label="Share on LinkedIn"
                >
                  <Icon name="linkedin" className="!size-3.5 fill-current" aria-hidden />
                </a>
                <a
                  href={`https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex size-9 items-center justify-center rounded-lg bg-primary text-white no-underline transition-opacity duration-150 hover:opacity-90"
                  aria-label="Share on X"
                >
                  <Icon name="twitter" className="!size-3.5 fill-current" aria-hidden />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex size-9 items-center justify-center rounded-lg bg-primary text-white no-underline transition-opacity duration-150 hover:opacity-90"
                  aria-label="Share on Facebook"
                >
                  <Icon name="facebook" className="!size-3.5 fill-current" aria-hidden />
                </a>
              </div>
            </div>
          )}

          {latestPosts && latestPosts.length > 0 && (
            <div>
              <h4 className={sectionTitleClass}>Latest Posts</h4>
              <div className="flex flex-col gap-0">
                {latestPosts.map((post, index) => (
                  <Link
                    key={index}
                    href={post.href}
                    className="group flex flex-col gap-0.5 py-2.5 no-underline transition-opacity duration-150 first:pt-0 last:pb-0 hover:opacity-100"
                  >
                    <span className="line-clamp-2 font-body text-[13px] font-medium leading-snug text-dark transition-colors duration-150 group-hover:text-primary">
                      {post.title}
                    </span>
                    <span className="mt-0.5 font-body text-[11px] font-normal leading-none text-[#555555]">
                      {post.author} · {post.date}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>
  );
};

export default TableOfContents;
